import React, { useState } from 'react';
import {
  Box, VStack, Heading, Text, Button, ButtonText, Spinner, Alert, Icon, ScrollView,
  RadioGroup, Radio, RadioIndicator, RadioIcon, RadioLabel, CircleIcon, Textarea, TextareaInput,
  Checkbox, CheckboxGroup, CheckboxIndicator, CheckboxIcon, CheckboxLabel, CheckIcon,
} from '@gluestack-ui/themed';
import { AlertCircle } from 'lucide-react-native';
import { useProcessStage3ContentStudioStage3Post } from '@/common/api/endpoints/content-studio/content-studio';
import { QuestionAnswer } from '@/common/api/models';
import { useContentStudioStore } from './store';
import { LoadingOverlay } from './LoadingOverlay';

export const Stage4ModuleStrategy = () => {
  const {
    stage2Output, stage2Input, setStage3Output, setStage, setStage3Input, setModuleDraft, config,
  } = useContentStudioStore();
  const [answers, setAnswers] = useState<QuestionAnswer[]>([]);

  const { mutate, isPending, isError } = useProcessStage3ContentStudioStage3Post({
    mutation: {
      onSuccess: (response) => {
        setStage3Output(response.data);
        setModuleDraft(response.data.moduleDraft);
        setStage(5);
      },
    },
  });

  if (!stage2Output) return null;

  const handleAnswer = (questionId: string, value: string | string[], type: 'text' | 'option') => {
    setAnswers((prev) => {
      const existing = prev.filter((a) => a.questionId !== questionId);
      const newAnswer: QuestionAnswer = { questionId };
      if (type === 'text') newAnswer.textResponse = value as string;
      if (type === 'option') {
        newAnswer.selectedOptionIds = Array.isArray(value) ? value : [value];
      }
      return [...existing, newAnswer];
    });
  };

  const handleSubmit = () => {
    const configAnswers: QuestionAnswer[] = [
      { questionId: 'config_levels', textResponse: String(config.moduleDepth) },
      { questionId: 'config_total_tasks', textResponse: String(config.totalVolume) },
      { questionId: 'config_difficulty', selectedOptionIds: [config.difficulty.toLowerCase()] },
      { questionId: 'config_study_program', textResponse: config.studyProgram },
    ];

    const payload = {
      answers: [...configAnswers, ...answers],
      selectedNodeIds: stage2Input.selectedNodeIds || [],
      originalPrompt: stage2Input.originalPrompt || '',
      scopeSummary: stage2Output.scopeSummary || '',
      config: {
        levels: config.moduleDepth,
        totalTasks: config.totalVolume,
        difficulty: config.difficulty.toLowerCase(),
        studyProgram: config.studyProgram,
      },
    };
    setStage3Input(payload);
    mutate({ data: payload });
  };

  return (
    <Box flex={1} p="$6" maxWidth={800} alignSelf="center" w="100%">
      <LoadingOverlay isLoading={isPending} title="Generating Module Draft" />
      <ScrollView>
        <VStack space="xl" pb="$10">
          <Box>
            <Heading size="xl">Module Strategy</Heading>
            <Text color="$textSubtle" mt="$2">
              Define the teaching style and structure for your module.
            </Text>
          </Box>

          {/* Scope Summary */}
          <Box p="$4" bg="$info100" borderRadius="$md" borderWidth={1} borderColor="$info200">
            <Heading size="xs" color="$info800" textTransform="uppercase">Locked Scope</Heading>
            <Text size="sm" mt="$1" color="$info900">{stage2Output.scopeSummary}</Text>
          </Box>

          {/* Strategy Questions */}
          {stage2Output.strategyQuestions && stage2Output.strategyQuestions.length > 0 && (
            <VStack space="lg">
              {stage2Output.strategyQuestions
                .filter((q) => !['config_levels', 'config_total_tasks', 'config_difficulty', 'config_study_program'].includes(q.id))
                .map((q) => (
                  <Box key={q.id} p="$4" bg="$bgSubtle" borderRadius="$md">
                    <Text fontWeight="bold" mb="$2">{q.text}</Text>
                    {q.helpText && <Text size="xs" color="$textSubtle" mb="$2">{q.helpText}</Text>}

                    {q.type === 'single_choice' && q.options && (
                      <RadioGroup
                        value={answers.find((a) => a.questionId === q.id)?.selectedOptionIds?.[0]}
                        onChange={(val) => handleAnswer(q.id, val, 'option')}
                      >
                        <VStack space="sm">
                          {q.options.map((opt) => (
                            <Radio key={opt.id} value={opt.id} size="sm">
                              <RadioIndicator mr="$2"><RadioIcon as={CircleIcon} /></RadioIndicator>
                              <RadioLabel>{opt.label}</RadioLabel>
                            </Radio>
                          ))}
                        </VStack>
                      </RadioGroup>
                    )}

                    {q.type === 'multiple_choice' && q.options && (
                      <CheckboxGroup
                        value={answers.find((a) => a.questionId === q.id)?.selectedOptionIds || []}
                        onChange={(val) => handleAnswer(q.id, val, 'option')}
                      >
                        <VStack space="sm">
                          {q.options.map((opt) => (
                            <Checkbox key={opt.id} value={opt.id} size="sm">
                              <CheckboxIndicator mr="$2"><CheckboxIcon as={CheckIcon} /></CheckboxIndicator>
                              <CheckboxLabel>{opt.label}</CheckboxLabel>
                            </Checkbox>
                          ))}
                        </VStack>
                      </CheckboxGroup>
                    )}

                    {q.type === 'text' && (
                      <Textarea>
                        <TextareaInput
                          placeholder="Type your answer..."
                          onChangeText={(val) => handleAnswer(q.id, val, 'text')}
                        />
                      </Textarea>
                    )}
                  </Box>
                ))}
            </VStack>
          )}

          {isError && (
            <Alert action="error" variant="solid" borderRadius="$md">
              <Icon as={AlertCircle} mr="$3" color="$textOnError" />
              <Text color="$textOnError">Failed to generate module. Please try again.</Text>
            </Alert>
          )}

          <Button
            onPress={handleSubmit}
            isDisabled={isPending}
            size="lg"
            mt="$4"
          >
            {isPending && <Spinner color="$white" mr="$2" />}
            <ButtonText>{isPending ? 'Generating Module...' : 'Generate Module Draft'}</ButtonText>
          </Button>
        </VStack>
      </ScrollView>
    </Box>
  );
};
